import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { History } from 'lucide-react';
import { useDeactivateUser, useActivateUser } from '../../hooks/useUsers';
import { useAuth } from '../../contexts/AuthContext';
import AuditDrawer from '../audit/AuditDrawer';
import type { User } from '../../types/user';

interface UserActionsProps {
  user: User;
  onEdit: (user: User) => void;
}

const UserActions: React.FC<UserActionsProps> = ({ user, onEdit }) => {
  const { user: currentUser } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
  });
  const deactivateUser = useDeactivateUser();
  const activateUser = useActivateUser();

  const isCurrentUser = currentUser?.userId === user.userId;

  const handleDeactivate = async () => {
    try {
      await deactivateUser.mutateAsync(user.userId);
      setShowConfirm(false);
      setShowMenu(false);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error al desactivar usuario');
    }
  };

  const handleActivate = async () => {
    try {
      await activateUser.mutateAsync(user.userId);
      setShowMenu(false);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error al activar usuario');
    }
  };

  const handleViewHistory = () => {
    setAuditDrawer({
      isOpen: true,
      userId: user.userId,
      userName: `${user.firstName} ${user.lastName}`,
    });
    setShowMenu(false);
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      userId: '',
      userName: '',
    });
  };

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 192; // w-48
      const menuHeight = 160; // approximate max height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;

      // Prevent menu from going off-screen
      if (left < 8) left = 8;
      if (left + menuWidth > viewportWidth - 8) left = viewportWidth - menuWidth - 8;
      if (top + menuHeight > viewportHeight - 8) top = rect.top - menuHeight - 4;

      setMenuPos({ top, left });
    }
  }, [showMenu]);

  // Close menu on scroll or resize
  useEffect(() => {
    if (!showMenu) return;
    const close = () => setShowMenu(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [showMenu]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowMenu(!showMenu)}
        className="text-gray-400 hover:text-gray-600"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {showMenu && createPortal(
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div
            ref={menuRef}
            className="fixed w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <div className="py-1">
              <button
                onClick={handleViewHistory}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <History className="w-4 h-4" />
                Ver Historial
              </button>
              
              <button
                onClick={() => {
                  onEdit(user);
                  setShowMenu(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Editar
              </button>

              {user.isActive ? (
                <button
                  onClick={() => {
                    if (isCurrentUser) {
                      alert('No puedes desactivarte a ti mismo');
                      return;
                    }
                    setShowConfirm(true);
                  }}
                  disabled={isCurrentUser}
                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Desactivar
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100"
                >
                  Activar
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowConfirm(false)}
            />

            <div className="relative bg-white rounded-lg max-w-lg w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                Confirmar Desactivación
              </h3>
              <p className="text-sm text-gray-600 mb-2 text-center">
                ¿Estás seguro de que deseas desactivar a{' '}
                <span className="font-medium">
                  {user.firstName} {user.lastName}
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 mb-6 text-center">
                El usuario no podrá acceder al sistema hasta que sea reactivado.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={deactivateUser.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                >
                  {deactivateUser.isLoading ? 'Desactivando...' : 'Desactivar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="USER"
        entityId={auditDrawer.userId}
        entityName={auditDrawer.userName}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </div>
  );
};

export default UserActions;
